/**
 * Web motion for the three-pane workspace (sidebar | agent chat | panel).
 *
 * Layout changes (a page opening beside the chat, the canvas panel for a
 * document, slides or an image, expanding it) play as a View Transition: the
 * browser snapshots the old and new layouts and morphs between them, so the
 * chat column resizes while the panel slides in or out. Page changes inside an
 * already open panel play a short enter animation instead. The keyframes and
 * the per-kind choreography live in `web-motion.css`; the shell decides which
 * kind to play (`classifyWorkspaceTransition`) and runs it.
 *
 * Everything degrades to the old instant swap when the browser has no View
 * Transitions or the user prefers reduced motion. Kept free of React so the
 * rules are unit-tested.
 */

export type WorkspaceTransitionKind =
  | 'panel-open'
  | 'panel-close'
  | 'artifact-open'
  | 'artifact-close'
  | 'artifact-swap'
  | 'artifact-resize'
  | 'layout';

export interface WorkspaceSnapshot {
  /** `getWebWorkspaceClassName` output. */
  className: string;
  /** The artifact the canvas panel shows, if any. */
  artifactId: string | null;
}

/** `<html data-tabbio-vt>`: present only while a workspace transition runs. */
export const WORKSPACE_TRANSITION_ATTRIBUTE = 'data-tabbio-vt';

function hasClass(className: string, token: string) {
  return className.split(/\s+/).includes(token);
}

/**
 * Which transition a workspace change plays, or null when the layout did not
 * change (a version or view switch inside the same open file, a route change
 * that keeps the split).
 */
export function classifyWorkspaceTransition(
  previous: WorkspaceSnapshot,
  next: WorkspaceSnapshot,
): WorkspaceTransitionKind | null {
  if (!previous.artifactId && next.artifactId) return 'artifact-open';
  if (previous.artifactId && !next.artifactId) return 'artifact-close';
  if (previous.artifactId && next.artifactId && previous.artifactId !== next.artifactId) {
    return 'artifact-swap';
  }
  if (previous.className === next.className) return null;
  if (
    hasClass(previous.className, 'is-artifact-expanded') !==
    hasClass(next.className, 'is-artifact-expanded')
  ) {
    return 'artifact-resize';
  }
  const wasChatOnly = hasClass(previous.className, 'is-chat-only');
  const isChatOnly = hasClass(next.className, 'is-chat-only');
  if (wasChatOnly && !isChatOnly) return 'panel-open';
  if (!wasChatOnly && isChatOnly) return 'panel-close';
  return 'layout';
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

type ViewTransitionLike = {
  finished: Promise<void>;
  skipTransition: () => void;
};

type DocumentWithViewTransitions = Document & {
  startViewTransition?: (update: () => Promise<void> | void) => ViewTransitionLike;
};

/** True when a workspace change may animate as a View Transition. */
export function canAnimateWorkspace(): boolean {
  if (typeof document === 'undefined') return false;
  const doc = document as DocumentWithViewTransitions;
  return (
    typeof doc.startViewTransition === 'function' &&
    doc.visibilityState !== 'hidden' &&
    !prefersReducedMotion()
  );
}

/**
 * Run `update` as a View Transition tagged with `kind` (CSS picks the
 * choreography from `<html data-tabbio-vt>`). `update` must resolve once the
 * new layout is committed. Returns the running transition, or null when it
 * could not start (then `update` has already run, unanimated).
 */
export function startWorkspaceTransition(
  kind: WorkspaceTransitionKind,
  update: () => Promise<void>,
): ViewTransitionLike | null {
  const doc = document as DocumentWithViewTransitions;
  const root = doc.documentElement;
  if (typeof doc.startViewTransition !== 'function') {
    void update();
    return null;
  }
  // Named before the old snapshot is taken: the panels only carry a
  // view-transition-name while this attribute is set (web-motion.css).
  root.setAttribute(WORKSPACE_TRANSITION_ATTRIBUTE, kind);
  try {
    const transition = doc.startViewTransition(update);
    // `finished` rejects only if `update` throws; the attribute goes either way.
    transition.finished
      .catch(() => {})
      .finally(() => {
        if (root.getAttribute(WORKSPACE_TRANSITION_ATTRIBUTE) === kind) {
          root.removeAttribute(WORKSPACE_TRANSITION_ATTRIBUTE);
        }
      });
    return transition;
  } catch {
    root.removeAttribute(WORKSPACE_TRANSITION_ATTRIBUTE);
    void update();
    return null;
  }
}

export type ContentNavigationDirection = 'forward' | 'back';

/**
 * A page change inside the open panel: the new page eases in from the side
 * the user is heading (back comes from the start edge). Opacity and a short
 * shift only, so it never reflows the page.
 */
export function playPanelContentEnter(
  element: Element | null | undefined,
  direction: ContentNavigationDirection,
): void {
  if (!element || typeof (element as HTMLElement).animate !== 'function') return;
  if (prefersReducedMotion()) return;
  const rtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
  const towardEnd = direction === 'forward' ? 1 : -1;
  const shift = 12 * towardEnd * (rtl ? -1 : 1);
  (element as HTMLElement).animate(
    [
      { opacity: 0, transform: `translateX(${shift}px)` },
      { opacity: 1, transform: 'translateX(0)' },
    ],
    { duration: 220, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
  );
}
